<template>
  <div class="well">
    <div class="search-filter">
      <h2 class="filter-header">Subjects</h2>
      <A11yDropdown
        :dropdown-props="{
          ref:'subject-dropdown',
          class: 'browse-subject-dropdown',
        }"
        :button-props="{
          class: 'btn btn-default dropdown-toggle ellipsis',
          'data-role': 'trigger',
          'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Choose a subject'
        }"
        :dom-attributes="{
          role: 'listbox',
          'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Choose a subject'
        }"
      >
        <template slot="button-contents">
          {{ subjectFilterText }} <i class="fa fa-caret-down" />
        </template>
        <template slot="dropdown-items">
          <li
            v-for="subject in subjectOptions"
            :key="subject"
            role="option"
            @click="setSubject(subject)"
            :class="{
              'cursor-not-allowed': isSubjectLocked(subject),
              'locks': areSubjectsLocked
            }"
          >
            <LockedOption v-if="isSubjectLocked(subject)" :label="subject" />
            <a
              v-else
              class="subject-option"
              :title="subject">
              {{ subject }}
            </a>
          </li>
        </template>
      </A11yDropdown>
    </div>
    <div class="search-filter">
      <fieldset>
        <legend id="grades-options-label"><h2 class="filter-header">Grades</h2></legend>
        <ol class="list-unstyled" aria-labelledby="grades-options-label">
          <li
            class="grade-checkbox"
            v-for="grade in gradeOptions"
            :key="grade">
            <label
              :is="isSubjectGradeLocked(selectedSubject, grade) ? 'span' : 'label'"
              class="label-toggle"
              :class="{
                disabled: isGradeDisabled(grade),
                selected: selectedGrades.indexOf(grade) > -1,
                'adult-transition': grade === 'Adult-Transition'
              }">
              <LockableInputWrapper :locked="isSubjectGradeLocked(selectedSubject, grade)">
                <input
                  v-if="!isSubjectGradeLocked(selectedSubject, grade)"
                  type="checkbox"
                  :disabled="isGradeDisabled(grade)"
                  :value="grade"
                  v-model="selectedGrades">
              </LockableInputWrapper>
              {{ getGradeLabel(grade) }}
            </label>
          </li>
        </ol>
      </fieldset>
    </div>
    <div class="search-filter">
      <h2 id="level-support-label" class="filter-header">Level of Support</h2>
      <vue-slider
        :rail-style="{ backgroundColor: 'var(--gray-weak)', borderRadius: '0' }"
        :process-style="{ backgroundColor: 'var(--blue-default)', borderRadius: '0' }"
        class="level-slider"
        :min="0"
        :max="2"
        :interval="1"
        :marks="{ 0: 'Less', 1: '', 2: 'More' }"
        :style="{ padding: '7px 7px 7px 0' }"
        :lazy="true"
        :contained="true"
        :label-style="{ fontSize: '12px', 'margin-left': '6px' }"
        :duration="0.1"
        :dot-attrs="{
          'role': 'slider',
          'aria-labelledby': 'level-support-label',
          'aria-valuenow': selectedLevelOfSupport,
          'aria-valuetext': selectedLevelOfSupportDescription,
          'aria-valuemin': 0,
          'aria-valuemax': 2,
        }"
        tooltip="none"
        v-model="selectedLevelOfSupport"
        @change="changedLevelOfSupport"
      >
        <template v-slot:dot="{ disabled, focus }">
          <div :class="['level-slider-custom-dot', { disabled, focus }]" />
        </template>
        <template v-slot:step="{ active }">
          <div :class="['level-slider-custom-step', { active }]" />
        </template>
      </vue-slider>
    </div>
    <div class="search-filter">
      <fieldset>
        <legend id="categories-options-label"><h2 class="filter-header">Categories</h2></legend>
        <p class="select-a-subject text-muted" v-if="!selectedSubject">Select a subject</p>
        <ul
          class="list-unstyled"
          v-if="domainOptions && domainOptions.length"
          aria-labelledby="categories-options-label">
          <template v-for="(domain, i) in domainOptions">
            <li
              v-if="selectedSubject !== ''"
              :id="`subject-domain-${i}`"
              class="subject-domain-option"
              :class="{
                disabled: isDomainDisabled(domain),
                selected: selectedSubjectDomain === domain,
              }"
              @click="!isSubjectDomainLocked(selectedSubject, domain) && toggleDomain(domain)"
              :key="domain">
              <LockableInputWrapper
                :locked="isSubjectDomainLocked(selectedSubject, domain)"
                :display-value="domain">
                <input
                  type="radio"
                  name="subject-domain"
                  :value="domain"
                  tabindex="0"
                  v-model="selectedSubjectDomain"
                  :disabled="isDomainDisabled(domain)"
                  :aria-labelledby="`subject-domain-${i}`"
                >
              </LockableInputWrapper>
            </li>
          </template>
        </ul>
      </fieldset>
      <p v-if="selectedSubject && (!domainOptions || !domainOptions.length)" class="text-muted">
        <i>No applicable categories</i>
      </p>
    </div>
    <div class="search-filter">
      <h2 class="filter-header" id="standards-options-label">Standards</h2>
      <p class="select-a-subject text-muted" v-if="!selectedSubject">Select a subject</p>
      <BrowseStandardSelector
        v-if="selectedSubject"
        :query="query"
        :standards="filterOptions.standards"
        :standard-selector-label-id="standardSelectorLabelId"
      />
    </div>
    <div class="search-filter">
      <label for="browse-search-input" class="filter-header">Search</label>
      <SearchInput
        :hide-button="true"
        :initial-query="keyword"
        :debounce-duration-ms="debounceTime"
        input-id="browse-search-input"
        @query:changed="keywordUpdate"
        :byte-limit-enforced="true"
        :byte-limit="algoliaByteLimit"
        byte-limit-message="Please use a shorter search query."
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import SearchInput from '@/components/search/SearchInput'
import BrowseStandardSelector from '@/components/search/BrowseStandardSelector'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js'
import A11yDropdown from '@/components/A11yDropdown'
import LockableInputWrapper from '@/components/LockableInputWrapper.vue'
import { ALGOLIA_BYTE_LIMIT } from '@/util/browse'
import { PremiumLocksMixin } from '@/contentprofile/premium_locks_mixin'
import LockedOption from '@/components/LockedOption'
import { gradeLabel } from '@/util/gradesort'

import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

export default {
  name: 'BrowseFilterSelector',
  components: {
    BrowseStandardSelector,
    VueSlider,
    SearchInput,
    A11yDropdown,
    LockableInputWrapper,
    LockedOption
  },
  mixins: [RedundantRouteMixin, PremiumLocksMixin],
  props: {
    query: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  computed: {
    ...mapGetters({
      filterOptions: 'browse/filterOptions',
      loadingResults: 'browse/loadingResults',
      enabledGrades: 'browse/enabledGrades',
      enabledDomains: 'browse/enabledDomains',
      contentProfileSubjects: 'contentProfile/publishedSubjects',
      standardSetsForSubject: 'contentProfile/visibleStandardSetsForSubject',
    }),
    ...mapState('browse', {
      contentProfileSubject: state => state.contentProfileSubject
    }),
    ...mapState('contentProfile', {
      activeProfileId: state => state.activeProfile.id,
      allStandardSets: state => state.activeProfile.standard_sets
    }),
    gradeOptions() {
      return this.query.subject
        ? this.contentProfileSubject && this.contentProfileSubject.grades
        : this.filterOptions.grades
    },
    subjectOptions() {
      return this.contentProfileSubjects.map(subject => {
        return subject.subject
      })
    },
    domainOptions() {
      return this.contentProfileSubject && this.contentProfileSubject.categories
    },
    selectedSubject: {
      get() {
        return this.subjectOptions.includes(this.query.subject) ? this.query.subject : ''
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)
        newQuery.subject = value
        delete newQuery.subjectDomain
        delete newQuery.standard
        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowseGoalsPage'
        })
      }
    },
    selectedSubjectDomain: {
      get() {
        return this.query.subjectDomain || ''
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)

        if (value) newQuery.subjectDomain = value
        else delete newQuery.subjectDomain

        delete newQuery.standard

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowseGoalsPage'
        })
      }
    },
    selectedLevelOfSupport: {
      get() {
        return this.query.levelOfSupport || 0
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)
        newQuery.levelOfSupport = value

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowseGoalsPage'
        })
      }
    },
    selectedLevelOfSupportDescription() {
      const levelDescriptions = {
        0: 'Less Support',
        1: 'Moderate Support',
        2: 'More Support'
      }
      return levelDescriptions[this.selectedLevelOfSupport] || 'Less Support'
    },
    selectedGrades: {
      get() {
        return this.query.grades
          ? Array.isArray(this.query.grades)
            ? this.query.grades
            : [this.query.grades]
          : []
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)
        if (!newQuery.grades) newQuery.grades = []
        if (!Array.isArray(newQuery.grades)) {
          newQuery.grades = [newQuery.grades]
        }

        if (newQuery.grades.length > value.length) {
          this.$sendEvent('Unselected Browse Goals Grade', {
            browse_goals_unselected_grade: newQuery.grades.find(elem => !value.includes(elem))
          })
        } else if (newQuery.grades.length < value.length) {
          this.$sendEvent('Selected Browse Goals Grade', {
            browse_goals_selected_grade: value.find(elem => !newQuery.grades.includes(elem))
          })
        }

        if (newQuery.grades.length === 0) delete newQuery.grades

        newQuery.grades = value
        delete newQuery.standard
        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowseGoalsPage'
        })
      }
    },
    keyword() {
      return this.query.keyword
    },
    unlockFilters() {
      //must have subject or keyword selected
      return this.selectedSubject || this.keyword
    }
  },
  data() {
    return {
      subjectFilterText: 'Choose a subject',
      debounceTime: 800,
      standardSelectorLabelId: 'standards-option-label',
      algoliaByteLimit: ALGOLIA_BYTE_LIMIT,
    }
  },
  methods: {
    getGradeLabel(grade) {
      return gradeLabel(grade)
    },
    setSubject(subject) {
      this.selectedSubject = subject
      this.$sendEvent('Selected Browse Goals Subject', {
        browse_goals_selected_subject: subject
      })
    },
    toggleDomain(domain) {
      if (this.selectedSubjectDomain === domain) {
        this.selectedSubjectDomain = ''
        this.$sendEvent('Unselected Browse Goals Category', {
          browse_goals_unselected_category: domain
        })
      } else if (this.enabledDomains.indexOf(domain) > -1) {
        this.selectedSubjectDomain = domain
        this.$sendEvent('Selected Browse Goals Category', {
          browse_goals_selected_category: domain
        })
      }
    },
    keywordUpdate(value) {
      let newQuery = Object.assign({}, this.query)
      if (value) {
        newQuery.keyword = value

        this.$sendEvent('Searched Browse Goals Keyword', {
          browse_goals_searched_keyword: value.slice(0, 120) // limit length due to max size of field in db
        })
      } else {
        if (newQuery.keyword) {
          this.$sendEvent('Cleared Browse Goals Keyword', {
            browse_goals_cleared_keyword: newQuery.keyword.slice(0, 120) // limit length due to max size of field in db
          })
        }
        delete newQuery.keyword
      }
      this.rerouteIfRouteChanged(this.$route, {
        query: newQuery,
        name: 'BrowseGoalsPage'
      })

    },
    changedLevelOfSupport(value) {
      this.$sendEvent('Changed Browse Goals Level of Support', {
        browse_goals_filter_level_of_support: value
      })
    },
    checkDeeplink() {
      if (!this.subjectOptions.includes(this.query.subject)) {
        delete this.query.subject
      }
      if (this.query.subject || this.query.keyword) {
        this.$store.commit('browse/setInitialFilters', this.query)
        if (this.query.subject) {
          this.$store.commit(
            'browse/setCPSubject',
            this.contentProfileSubjects[this.subjectOptions.indexOf(this.query.subject)]
          )
          this.subjectFilterText = this.selectedSubject
        }
        let details = {
          standardSets: this.query.subject
            ? this.standardSetsForSubject(this.selectedSubject, {
                onlyPublished: true
              })
            : this.allStandardSets
        }
        this.$store.dispatch('browse/fetchGoals', details)
      }
    },
    isDomainDisabled(domain) {
      return this.enabledDomains.indexOf(domain) === -1 || this.isSubjectDomainLocked(this.selectedSubject, domain)
    },
    isGradeDisabled(grade) {
      return (
        this.enabledGrades.indexOf(grade) === -1
        || !this.unlockFilters
        || this.isSubjectGradeLocked(this.selectedSubject, grade)
      )
    },
    // The vue-slider-component library we use explicitly sets aria-valuetext to tooltipValue (we're not using the tooltip),
    // which is presumably the number value - we want screen readers to read the descriptive value so this is how we override that
    updateAriaValueText() {
      const sliderDot = this.$el.querySelector('.vue-slider-dot');
      sliderDot.setAttribute('aria-valuetext', this.selectedLevelOfSupportDescription);
    }
  },
  updated() {
    this.updateAriaValueText()
  },
  watch: {
    query(to, from) {
      let details = {
        standardSets: to.subject
          ? this.standardSetsForSubject(to.subject, {
              onlyPublished: true
            })
          : this.allStandardSets
      }
      if (to.subject !== from.subject) {
        this.subjectFilterText = to.subject ? this.selectedSubject : 'Select a subject'
        if (to.subject) {
          this.$store.commit(
            'browse/setCPSubject',
            this.contentProfileSubjects[this.subjectOptions.indexOf(to.subject)]
          )
        }
      }
      if (to.standard !== from.standard && to.standard !== '') {
        details.doNotRetrieveStandards = true
      }
      this.$store.commit('browse/setFilters', to)
      if (to.subject || to.keyword) {
        this.$store.dispatch('browse/fetchGoals', details)
      } else {
        this.subjectFilterText = 'Choose a subject'
        this.$store.commit('browse/setGoals', [])
      }
    },
    activeProfileId(to) {
      if (to) {
        this.checkDeeplink()
      }
    }
  },
  created() {
    if (this.activeProfileId) {
      this.checkDeeplink()
    }
  }
}
</script>

<style lang="less">
@import '~@/variables.less';
.browse-subject-dropdown {
  width: 100%;
  .btn.dropdown-toggle {
    width: 100%;
    text-align: left;
    padding-right: 20px;
    position: relative;

    .fa-caret-down {
      position: absolute;
      top: 9px;
      right: 8px;
    }
  }

  .dropdown-menu {
    min-width: 200px;
    @media (min-width: @screen-xs-max) and (max-width: @screen-md-max) {
      max-width: 100%;
    }

    .subject-option {
      white-space: normal;
      &:hover {
        background-color: @blue;
        color: white;
        cursor: pointer;
      }
    }
  }
}

.lock-button {
  border: 0;
  cursor: not-allowed;
}

.focus-styles {
  outline-color: lighten(@link-color, 20%);
  outline-offset: 4px;
  outline-style: auto;
}

.vue-slider-dot:focus-visible {
  .level-slider-custom-dot {
    .focus-styles;
  }
}
@supports not selector(:focus-visible) {
  .vue-slider-dot:focus {
    .level-slider-custom-dot {
      .focus-styles;      }
    }
  }
</style>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/components/search/browsestyles.less';
.well {
  position: relative;
}

.grade-checkbox {
  display: inline-block;
  margin: 2px;
  vertical-align: top;
  .label-toggle {
    text-align: center;
    width: 32px;
    padding: 4px 8px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    & input {
      display: block;
      margin: 2px auto;
    }
    .lockable-input-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 1.25rem;
    }
    &.adult-transition {
      width: 64px;
    }
  }
  /deep/ .lock-button {
    margin-left: 0;
  }
}
.select-a-subject {
  font-style: italic;
}
.level-slider {
  margin-bottom: 32px;
  &:hover {
    cursor: pointer;
  }
}
.level-slider-custom-dot {
  position: absolute;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--blue-default);
  background: var(--blue-default);
  &.disabled {
    background: #fff;
  }
}
.level-slider-custom-step {
  position: absolute;
  left: 50%;
  top: -122%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--gray-weak);
  background-color: #fff;

  &.active {
    box-shadow: 0 0 0 2px var(--blue-default);
  }
}
legend {
  margin-bottom: 0;
  border: none;
}
.locks .subject-option {
  padding-left: 26px;
}
/deep/ .lock-icon {
  margin-left: 3px;
}
</style>
